import { type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { WizardSessionProvider } from "@/hooks/useWizardSession";
import { useWizardSessionRestore } from "@/hooks/useWizardSessionRestore";
import { cn } from "@/lib/cn";
import { strings } from "@/lib/strings";
import { WIDE_LAYOUT_SEGMENTS } from "@/lib/steps";

interface AppShellProps {
  children?: ReactNode;
}

function AppShellContent({ children }: { children?: ReactNode }) {
  useWizardSessionRestore();
  return children ?? <Outlet />;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const segment = location.pathname.split("/").filter(Boolean).pop() ?? "";
  const wide = WIDE_LAYOUT_SEGMENTS.has(segment);
  const contentMax = wide ? "max-w-7xl" : "max-w-5xl";

  return (
    <WizardSessionProvider>
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex min-h-screen flex-1 flex-col">
          <Topbar />
          <main className="relative flex-1 overflow-hidden">
            <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-aurora opacity-30" />
            <div className={cn("mx-auto flex h-full w-full flex-col gap-6 p-6 lg:p-10", contentMax)}>
              <AppShellContent>{children}</AppShellContent>
            </div>
          </main>
          <footer className="border-t border-border/60 bg-background/50 px-6 py-3 text-xs text-muted-foreground backdrop-blur-xl">
            <div className={cn("mx-auto flex flex-wrap items-center justify-between gap-3", contentMax)}>
              <span>{strings.app.footer}</span>
            </div>
          </footer>
        </div>
      </div>
    </WizardSessionProvider>
  );
}
